import { useState, useEffect } from 'react';
import { X, Bell, CheckCircle2, AlertCircle, Info, ExternalLink, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { fetchUserPreferences, visibleInApp } from '../utils/notificationPreferences';
import type { UserNotificationPreferences } from '../types';

interface NotificationsPanelProps {
  userId: string;
  projectId: string;
  accessToken: string;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void; // Add callback for count changes
  onOpenQueueItems?: () => void; // Callback to open queue items panel
  onOpenSOW?: (sowId: string) => void; // Callback to open SOW modal
}

interface Notification {
  id: string;
  userId: string;
  type: 'client_response' | 'sow_update' | 'config_item' | 'system' | 'message' | 'item_update';
  title: string;
  message: string;
  link?: string;
  read: boolean;
  createdAt: string;
  timestamp?: string;
}

export function NotificationsPanel({ userId, projectId, accessToken, onClose, onUnreadCountChange, onOpenQueueItems, onOpenSOW }: NotificationsPanelProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [preferences, setPreferences] = useState<UserNotificationPreferences | null>(null);

  useEffect(() => {
    loadNotifications();
    // In-app preferences (Settings › Notifications) hide the categories the user turned off.
    fetchUserPreferences(userId, projectId, '').then(setPreferences).catch(() => setPreferences(null));
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/notifications`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[NOTIFICATIONS] Loaded notifications:', data.notifications);
        console.log('[NOTIFICATIONS] Sample notification:', data.notifications?.[0]);
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/notifications/${notificationId}/read`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setNotifications(notifications.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        ));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/user/notifications/read-all`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setNotifications(notifications.map(n => ({ ...n, read: true })));
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Delete one (RESTORE_PLAN R2.5: the old NotificationCenter's delete)
  const deleteNotification = async (notificationId: string) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/user/notifications/${encodeURIComponent(notificationId)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok || response.status === 404) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'client_response':
        return <AlertCircle className="size-5 text-blue-600" />;
      case 'sow_update':
        return <CheckCircle2 className="size-5 text-green-600" />;
      case 'config_item':
        return <Info className="size-5 text-orange-600" />;
      default:
        return <Bell className="size-5 text-gray-600" />;
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const visible = visibleInApp(notifications, preferences);
  const filteredNotifications = visible.filter(n =>
    filter === 'all' || (filter === 'unread' && !n.read)
  );

  const unreadCount = visible.filter(n => !n.read).length;

  // Notify parent whenever unread count changes
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  const handleViewDetails = (notification: Notification) => {
    console.log('[NOTIFICATIONS] View details clicked:', notification);
    console.log('[NOTIFICATIONS] Notification link:', notification.link);
    
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Extract SOW ID or queue item info from link
    if (notification.link) {
      console.log('[NOTIFICATIONS] Processing link:', notification.link);
      if (notification.link.startsWith('#sow-')) {
        const sowId = notification.link.replace('#sow-', '');
        console.log('[NOTIFICATIONS] Opening SOW modal with ID:', sowId);
        if (onOpenSOW) {
          onOpenSOW(sowId);
        }
      } else if (notification.link.startsWith('#queue-item-') || notification.link === '#') {
        console.log('[NOTIFICATIONS] Opening queue items panel');
        if (onOpenQueueItems) {
          onOpenQueueItems();
        }
      } else {
        console.log('[NOTIFICATIONS] Navigating to:', notification.link);
        // Fallback to navigation for other links
        window.location.href = notification.link;
      }
    } else {
      console.log('[NOTIFICATIONS] No link found on notification');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bell className="size-6" />
            <h2 className="text-2xl">Notifications</h2>
            {unreadCount > 0 && (
              <Badge variant="destructive">{unreadCount}</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                Mark all read
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="size-5" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={filter} onValueChange={(v) => setFilter(v as 'all' | 'unread')} className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-4 flex-shrink-0">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="all">
                All ({visible.length})
              </TabsTrigger>
              <TabsTrigger value="unread">
                Unread ({unreadCount})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Content - now with proper scrolling */}
          <TabsContent value={filter} className="flex-1 overflow-y-auto px-6 pb-6 mt-4 min-h-0">
            {loading ? (
              <div className="py-12 text-center text-muted-foreground">
                Loading notifications...
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <Bell className="size-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No notifications</p>
                <p className="text-sm">
                  {filter === 'unread' ? "No unread notifications" : "No notifications yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-3 pb-2">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      !notification.read ? 'bg-blue-50 border-blue-200' : 'bg-white'
                    }`}
                  >
                    <div 
                      className="flex gap-3 cursor-pointer hover:opacity-80"
                      onClick={() => {
                        if (!notification.read) {
                          markAsRead(notification.id);
                        }
                      }}
                    >
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-medium">{notification.title}</h4>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                            {formatTime(notification.createdAt || notification.timestamp || '')}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-red-600"
                              aria-label={`Delete notification: ${notification.title}`}
                              title="Delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                void deleteNotification(notification.id);
                              }}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {notification.message}
                        </p>
                      </div>
                    </div>
                    {notification.link && (
                      <div className="mt-3 pt-3 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 -ml-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(notification);
                          }}
                        >
                          <ExternalLink className="size-3 mr-1.5" />
                          View details
                          <ChevronRight className="size-3 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
